import { CREATE_DOCUMENT_STRING } from 'apis/HealthieApiClient'
import { getHealthieRequest, showErrorMessage } from './careHooks'
import { useI18n } from 'contexts/AuthContext'
import _ from 'lodash'

const compressImage = (file, maxSizeInBytes) => {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      resolve(file)
      return
    }

    if (file.size <= maxSizeInBytes) {
      resolve(file)
      return
    }

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()

    img.onload = () => {
      let { width, height } = calculateCompressedDimensions(
        img.width,
        img.height,
        maxSizeInBytes
      )

      canvas.width = width
      canvas.height = height

      ctx.drawImage(img, 0, 0, width, height)

      const tryCompress = (quality) => {
        canvas.toBlob(
          (blob) => {
            if (blob.size <= maxSizeInBytes || quality <= 0.3) {
              const compressedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              })
              resolve(compressedFile)
            } else {
              tryCompress(quality - 0.05)
            }
          },
          file.type,
          quality
        )
      }

      tryCompress(0.95)
    }

    img.onerror = () => {
      resolve(file)
    }

    img.src = URL.createObjectURL(file)
  })
}

const calculateCompressedDimensions = (
  originalWidth,
  originalHeight,
  maxSizeInBytes
) => {
  const originalSize = originalWidth * originalHeight * 4
  const compressionRatio = Math.sqrt(maxSizeInBytes / originalSize)

  const ratio = Math.max(0.1, Math.min(1, compressionRatio))

  return {
    width: Math.round(originalWidth * ratio),
    height: Math.round(originalHeight * ratio),
  }
}

export const useUploadDocument = () => {
  const { i18n } = useI18n()
  const uploadDocument = async (file, clientId, options = {}) => {
    const { displayName, shareWithRel = true } = options

    try {
      const compressedFile = await compressImage(file, 800 * 1024) // 800KB

      const fileBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          const base64String = reader.result
          resolve(base64String)
        }
        reader.onerror = reject
        reader.readAsDataURL(compressedFile)
      })

      const variables = {
        file_string: fileBase64,
        rel_user_id: clientId,
        display_name: displayName,
        share_with_rel: shareWithRel,
        user_id: clientId,
      }

      const { error, data } = await getHealthieRequest({
        queryString: CREATE_DOCUMENT_STRING,
        variables: variables,
        operationName: 'createDocument',
      })
      if (data) {
        const { createDocument, msg } = data
        const { document, messages } = createDocument || {}
        if (!_.isEmpty(messages) || msg) {
          showErrorMessage(messages || { msg }, i18n)
          return {
            success: false,
            messages: messages || { msg },
          }
        } else {
          return {
            success: true,
            document: document,
          }
        }
      } else if (error) {
        showErrorMessage(error, i18n)
        return {
          success: false,
          messages: error,
        }
      }
    } catch (error) {
      showErrorMessage(error, i18n)
      return {
        success: false,
        messages: error,
      }
    }
  }

  return { uploadDocument }
}
